import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip,
  Cell, PieChart, Pie, RadialBarChart, RadialBar, Legend,
} from "recharts";
import {
  Home, Wheat, Brain, Zap, DollarSign, Clock, CheckCircle,
  BarChart3, Shield, Link, FlaskConical, Cpu, Loader2, RefreshCw,
  ArrowUpRight, TrendingUp, Activity, Hash,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  haven: "#10b981",
  harvest: "#f59e0b",
  other: "#6366f1",
  quantum: "#8b5cf6",
};

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)]"
      data-testid={`cv-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[var(--qn-foreground-muted)]">{label}</span>
      </div>
      <div className="text-xl font-outfit font-bold text-[var(--qn-foreground)]">{value}</div>
      {sub && <div className="text-[10px] text-[var(--qn-foreground-subtle)] mt-1">{sub}</div>}
    </motion.div>
  );
}

function VerticalCard({ name, focus, color, icon: Icon, data }) {
  const pct = data.success_rate || 0;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-xl border bg-[var(--qn-card)] overflow-hidden"
      style={{ borderColor: `${color}30` }}
      data-testid={`cv-vertical-${name.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="p-4 border-b border-[var(--qn-border)]/30" style={{ background: `${color}06` }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[var(--qn-foreground)]">{name}</h3>
            <p className="text-[10px] text-[var(--qn-foreground-muted)]">{focus} — {data.agents || "N/A"} agents, {data.divisions || "N/A"} divisions</p>
          </div>
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 gap-3">
        <div>
          <span className="text-[10px] text-[var(--qn-foreground-muted)]">Executions</span>
          <div className="text-lg font-bold font-mono text-[var(--qn-foreground)]">{data.total_executions || 0}</div>
        </div>
        <div>
          <span className="text-[10px] text-[var(--qn-foreground-muted)]">Success Rate</span>
          <div className="text-lg font-bold font-mono" style={{ color: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }}>{pct}%</div>
        </div>
        <div>
          <span className="text-[10px] text-[var(--qn-foreground-muted)]">Total Cost</span>
          <div className="text-sm font-mono text-[var(--qn-foreground)]">${(data.total_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <span className="text-[10px] text-[var(--qn-foreground-muted)]">Tokens</span>
          <div className="text-sm font-mono text-[var(--qn-foreground)]">{(data.total_tokens || 0).toLocaleString()}</div>
        </div>
        <div>
          <span className="text-[10px] text-[var(--qn-foreground-muted)]">Avg Duration</span>
          <div className="text-sm font-mono text-[var(--qn-foreground)]">{data.avg_duration_ms ? `${(data.avg_duration_ms / 1000).toFixed(1)}s` : "--"}</div>
        </div>
        <div>
          <span className="text-[10px] text-[var(--qn-foreground-muted)]">Last 30 Days</span>
          <div className="text-sm font-mono text-[var(--qn-foreground)]">{data.last_30_days || 0} runs</div>
        </div>
      </div>
      {data.recent_executions?.length > 0 && (
        <div className="px-4 pb-4">
          <div className="text-[10px] text-[var(--qn-foreground-muted)] mb-2">Recent Executions</div>
          <div className="space-y-1.5">
            {data.recent_executions.slice(0, 3).map((e, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] p-2 rounded-lg bg-[var(--qn-surface)]/30">
                <span className="text-[var(--qn-foreground)] font-medium truncate max-w-[180px]">{e.swarm_name || "Execution"}</span>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[8px] ${e.status === "completed" ? "bg-emerald-600" : e.status === "running" ? "bg-blue-600" : "bg-red-600"}`}>{e.status}</Badge>
                  <span className="text-[var(--qn-foreground-muted)]">${Number(e.total_cost || 0).toFixed(4)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function CrossVerticalDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cross-vertical/intelligence`);
      setData(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="cross-vertical-dashboard">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--qn-foreground-muted)] mr-2" />
        <span className="text-sm text-[var(--qn-foreground-muted)]">Loading Cross-Vertical Intelligence...</span>
      </div>
    );
  }

  if (!data) return null;

  const { verticals, grand_total, quantum, blockchain, ab_testing, compliance } = data;

  // Chart data
  const executionComparison = [
    { name: "HAVEN", executions: verticals.haven.total_executions, fill: COLORS.haven },
    { name: "HARVEST", executions: verticals.harvest.total_executions, fill: COLORS.harvest },
    { name: "Custom", executions: verticals.other.total_executions, fill: COLORS.other },
  ];

  const costBreakdown = [
    { name: "HAVEN", value: verticals.haven.total_cost, fill: COLORS.haven },
    { name: "HARVEST", value: verticals.harvest.total_cost, fill: COLORS.harvest },
    { name: "Custom", value: verticals.other.total_cost, fill: COLORS.other },
  ].filter(d => d.value > 0);

  const successRates = [
    { name: "HAVEN", rate: verticals.haven.success_rate, fill: COLORS.haven },
    { name: "HARVEST", rate: verticals.harvest.success_rate, fill: COLORS.harvest },
    { name: "Custom", rate: verticals.other.success_rate, fill: COLORS.other },
  ];

  return (
    <div className="space-y-6" data-testid="cross-vertical-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)] flex items-center gap-3">
            <Activity className="w-6 h-6 text-[var(--qn-primary)]" />
            Cross-Vertical Intelligence
          </h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">
            Combined analytics across HAVEN, HARVEST, and custom swarm executions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {compliance.map((c) => (
              <Badge key={c} variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400">{c}</Badge>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} className="h-8">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>
      </div>

      {/* Grand Total Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Brain} label="Total Agents" value={grand_total.total_agents.toLocaleString()} color="#3b82f6" sub={`${grand_total.total_divisions} divisions`} />
        <StatCard icon={Zap} label="Executions" value={grand_total.total_executions.toLocaleString()} color="#8b5cf6" sub={`${grand_total.last_30_days} last 30d`} />
        <StatCard icon={CheckCircle} label="Success Rate" value={`${grand_total.success_rate}%`} color="#10b981" sub={`${grand_total.completed} completed`} />
        <StatCard icon={DollarSign} label="Total Cost" value={`$${grand_total.total_cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color="#f59e0b" />
        <StatCard icon={Hash} label="Tokens" value={grand_total.total_tokens.toLocaleString()} color="#06b6d4" />
        <StatCard icon={Clock} label="Avg Duration" value={grand_total.avg_duration_ms ? `${(grand_total.avg_duration_ms / 1000).toFixed(1)}s` : "--"} color="#f97316" />
      </div>

      {/* Vertical Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <VerticalCard name="Project HAVEN" focus="Affordable Housing" color={COLORS.haven} icon={Home} data={verticals.haven} />
        <VerticalCard name="Project HARVEST" focus="Food Supply Chain" color={COLORS.harvest} icon={Wheat} data={verticals.harvest} />
        <VerticalCard name="Custom Swarms" focus="User-built workflows" color={COLORS.other} icon={Brain} data={verticals.other} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Execution Comparison */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cv-executions-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">Executions by Vertical</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={executionComparison}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="executions" radius={[6, 6, 0, 0]}>
                  {executionComparison.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Cost Breakdown */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cv-cost-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">Cost Distribution</CardTitle>
          </CardHeader>
          <CardContent className="pb-4 flex items-center justify-center">
            {costBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={costBreakdown} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {costBreakdown.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <RTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} formatter={(v) => `$${Number(v).toFixed(4)}`} />
                  <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: 10, color: "var(--qn-foreground-muted)" }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-xs text-[var(--qn-foreground-muted)] py-12">No cost data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Success Rates */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cv-success-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">Success Rates</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={successRates} layout="vertical">
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} axisLine={false} tickLine={false} width={60} />
                <RTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} formatter={(v) => `${v}%`} />
                <Bar dataKey="rate" radius={[0, 6, 6, 0]}>
                  {successRates.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Platform Intelligence Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Quantum Engine */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cv-quantum-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-purple-500/15">
                <Cpu className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--qn-foreground)]">Quantum Engine</h3>
                <p className="text-[10px] text-[var(--qn-foreground-muted)]">{quantum.engine} ({quantum.qubits.toLocaleString()} qubits)</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] text-[var(--qn-foreground-muted)]">Total Optimizations</span>
                <div className="text-lg font-bold font-mono text-[var(--qn-foreground)]">{quantum.total_optimizations.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-[10px] text-[var(--qn-foreground-muted)]">Last 30 Days</span>
                <div className="text-lg font-bold font-mono text-purple-400">{quantum.recent_optimizations.toLocaleString()}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Blockchain */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cv-blockchain-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15">
                <Link className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--qn-foreground)]">Blockchain Ledger</h3>
                <p className="text-[10px] text-[var(--qn-foreground-muted)]">{blockchain.network}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] text-[var(--qn-foreground-muted)]">Total Blocks</span>
                <div className="text-lg font-bold font-mono text-[var(--qn-foreground)]">{blockchain.total_blocks.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-[10px] text-[var(--qn-foreground-muted)]">Quantum Blocks</span>
                <div className="text-lg font-bold font-mono text-cyan-400">{blockchain.quantum_blocks.toLocaleString()}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* A/B Testing */}
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="cv-ab-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-500/15">
                <FlaskConical className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--qn-foreground)]">A/B Testing</h3>
                <p className="text-[10px] text-[var(--qn-foreground-muted)]">Controlled experiments on routing strategies</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] text-[var(--qn-foreground-muted)]">Active Tests</span>
                <div className="text-lg font-bold font-mono text-blue-400">{ab_testing.active_tests}</div>
              </div>
              <div>
                <span className="text-[10px] text-[var(--qn-foreground-muted)]">Total Tests</span>
                <div className="text-lg font-bold font-mono text-[var(--qn-foreground)]">{ab_testing.total_tests}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
